/**
 * KoDetailScreen (QGS v2)
 *
 * Full-screen detail of a single Knockout row. Reachable from the
 * operator dashboard's KOs tab (click a card). Shows every field
 * spec'd in the requirements (KO ID, form, product, operator,
 * approver, dates, defect type / location, comment, photo) plus a
 * "Mark as Reworked" affordance for the operator to submit the KO
 * for Line Leader closure approval.
 *
 * Rework flow implemented here:
 *   OPEN  ->  Operator fills rework_type + comment + optional photo url
 *         ->  POST /qgs/knockouts/{id}/request-closure/
 *         ->  KO stays OPEN but with closure_pending=true, appears in
 *             the LL's Approval Queue as a REWORK_CLOSURE item.
 *   OPEN + closure_pending=true -> screen switches to a "Awaiting Line
 *                                  Leader closure signature" state.
 *   CLOSED -> shows the closure metadata; no further action.
 */
import React, { useEffect, useState } from 'react';
import { ArrowLeft, Wrench, Clock, User, AlertOctagon, CheckCircle2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API = (process.env.REACT_APP_BASE_API_URL || 'http://localhost:8000') + '/api/frontend/qgs';


function fmtWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
}


async function csrf() {
  try {
    const r = await fetch(`${API.replace('/api/frontend/qgs','')}/api/frontend/auth/csrf-token/`, { credentials: 'include' });
    if (r.ok) return (await r.json())?.data?.csrfToken || '';
  } catch {}
  return '';
}


function StatusPill({ ko }) {
  if (ko.status === 'CLOSED') {
    return <Badge className="bg-green-100 text-green-800 border border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Closed</Badge>;
  }
  if (ko.status === 'DEFERRED') {
    return <Badge className="bg-orange-100 text-orange-800 border border-orange-200">Deferred</Badge>;
  }
  if (ko.status === 'PENDING_APPROVAL') {
    return <Badge className="bg-indigo-100 text-indigo-800 border border-indigo-200">Pending Approval</Badge>;
  }
  if (ko.closure_pending) {
    return <Badge className="bg-amber-100 text-amber-800 border border-amber-200">Closure Pending</Badge>;
  }
  return <Badge className="bg-red-100 text-red-800 border border-red-200">Open</Badge>;
}


export default function KoDetailScreen({ koRow, onBack, user }) {
  const [ko, setKo] = useState(null);
  const [reworkTypes, setReworkTypes] = useState([]);
  const [reworkTypeId, setReworkTypeId] = useState('');
  const [comment, setComment] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  // Hydrate the KO. The dashboard passes koRow with the reports/knockouts/
  // shape (has .id and .ko_id); we refetch the canonical Knockout row for
  // extra fields the reports view flattens away.
  useEffect(() => {
    (async () => {
      if (!koRow) return;
      try {
        const url = `${API}/knockouts/${koRow.id}/`;
        const r = await fetch(url, { credentials: 'include' });
        if (r.ok) setKo(await r.json());
        else setKo(koRow);
      } catch {
        setKo(koRow);
      }
    })();
    (async () => {
      try {
        const r = await fetch(`${API}/rework-types/?page_size=500`, { credentials: 'include' });
        if (r.ok) {
          const b = await r.json();
          setReworkTypes(b.results || b || []);
        }
      } catch {}
    })();
  }, [koRow]);

  if (!ko) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500">Loading KO…</div>
      </div>
    );
  }

  // QGS v2 · role split. Rework is the operator's job — submitting the
  // "Mark as Reworked" form should never be exposed to a Line Leader (they
  // would only get stuck against the backend's self-approval guard).
  // The LL sees a passive "awaiting operator" panel, or — when
  // closure_pending is true — an inline Approve / Reject panel that hits
  // the same /reports/approvals/CLOSURE-<id>/… endpoints the queue uses.
  const isLineLeader = user?.user_type === 'LINE_LEADER';
  const canRequestClosure = ko.status === 'OPEN' && !ko.closure_pending && !isLineLeader;
  const isClosureWaiting  = ko.status === 'OPEN' && ko.closure_pending && !isLineLeader;
  const showLlAwaiting    = ko.status === 'OPEN' && !ko.closure_pending && isLineLeader;
  const showLlSignoff     = ko.status === 'OPEN' && ko.closure_pending && isLineLeader;
  const isClosed          = ko.status === 'CLOSED';

  const submitClosure = async () => {
    setError(''); setOk('');
    if (!reworkTypeId) {
      setError('Pick a rework type first.');
      return;
    }
    setBusy(true);
    try {
      const token = await csrf();
      const res = await fetch(`${API}/knockouts/${ko.id}/request-closure/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-CSRFToken': token } : {}) },
        body: JSON.stringify({
          rework_type: Number(reworkTypeId),
          rework_comment: comment,
          after_rework_photo: photoUrl,
        }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b.error) msg = b.error; } catch {}
        throw new Error(msg);
      }
      const updated = await res.json();
      setKo(updated);
      setOk('Rework submitted. Waiting for Line Leader signature.');
    } catch (e) {
      setError(e.message || 'Failed to submit rework.');
    } finally {
      setBusy(false);
    }
  };

  // QGS v2 · Line Leader inline closure sign-off. Same endpoint the
  // Approval Queue calls, so the guard/side-effects match exactly.
  const decideClosure = async (decision) => {
    setError(''); setOk('');
    let reason = '';
    if (decision === 'reject') {
      reason = window.prompt('Reason for rejecting the closure?') || '';
      if (!reason.trim()) return;
    }
    setBusy(true);
    try {
      const token = await csrf();
      const res = await fetch(`${API}/reports/approvals/CLOSURE-${ko.id}/${decision}/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-CSRFToken': token } : {}) },
        body: JSON.stringify(decision === 'reject' ? { reason } : {}),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try { const b = await res.json(); if (b.error) msg = b.error; } catch {}
        throw new Error(msg);
      }
      // Refetch the KO so the screen switches to its CLOSED / rejected view.
      const fresh = await fetch(`${API}/knockouts/${ko.id}/`, { credentials: 'include' });
      if (fresh.ok) setKo(await fresh.json());
      setOk(decision === 'approve' ? 'Closure signed off. KO is now CLOSED.' : 'Closure rejected.');
    } catch (e) {
      setError(e.message || `Failed to ${decision} closure.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-4 flex items-center justify-between gap-3 sticky top-0 z-10">
        <div className="min-w-0">
          <h1 className="text-lg md:text-xl font-bold text-slate-900 truncate">
            <AlertOctagon className="inline w-5 h-5 text-red-600 mr-1" />
            {ko.ko_id || `KO #${ko.id}`}
          </h1>
          <p className="text-xs text-slate-500 truncate">{koRow?.form_name || ''}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <StatusPill ko={ko} />
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-6 space-y-4">
        {/* Metadata card */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-6 text-sm">
          <div><span className="text-slate-500">Unit:</span> <span className="font-mono">{koRow?.unit_serial || ko.unit_serial || '—'}</span></div>
          <div><span className="text-slate-500">Gate:</span> <span className="font-medium">{koRow?.gate || '—'}</span></div>
          <div><span className="text-slate-500">Product:</span> <span className="font-medium">{koRow?.product_model || '—'}</span></div>
          <div><span className="text-slate-500">Line:</span> <span className="font-medium">{koRow?.production_line || '—'}</span></div>
          <div><span className="text-slate-500">Defect type:</span> <span className="font-medium">{koRow?.defect_type || '—'}</span></div>
          <div><span className="text-slate-500">Defect location:</span> <span className="font-medium">{koRow?.defect_location || '—'}</span></div>
          <div className="md:col-span-2 flex items-center gap-2">
            <User className="w-4 h-4 text-slate-400" />
            <span className="text-slate-500">Raised by:</span>
            <span className="font-medium">{koRow?.creator || '—'}</span>
            <span className="text-slate-400">at</span>
            <span>{fmtWhen(ko.created_at || koRow?.created_at)}</span>
          </div>
          {ko.approved_by && (
            <div className="md:col-span-2 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-green-600" />
              <span className="text-slate-500">Approved by:</span>
              <span className="font-medium">{koRow?.approver || `user #${ko.approved_by}`}</span>
              <span className="text-slate-400">at</span>
              <span>{fmtWhen(ko.approved_at)}</span>
            </div>
          )}
          {ko.initial_comment && (
            <div className="md:col-span-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Initial comment</p>
              <p className="text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2">{ko.initial_comment}</p>
            </div>
          )}
          {ko.initial_photo && (
            <div className="md:col-span-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Photo</p>
              <a href={ko.initial_photo} target="_blank" rel="noreferrer" className="inline-block">
                <img src={ko.initial_photo} alt="KO evidence" className="max-h-48 rounded border border-slate-200" />
              </a>
            </div>
          )}
        </div>

        {/* QGS v2 · Line Leader passive panel — nothing to do until the
            operator submits rework. Replaces the operator's Mark-as-Reworked
            form entirely for LL sessions. */}
        {showLlAwaiting && (
          <div className="bg-white rounded-lg border border-slate-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" /> Awaiting operator rework
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              The operator on the line needs to complete the rework and submit it before you can sign anything.
              This KO will surface in your Approval Queue as soon as they do.
            </p>
          </div>
        )}

        {/* QGS v2 · Line Leader inline closure sign-off. Same endpoint the
            Approval Queue calls, so all the guards (self-approval, closure
            already handled) apply identically. */}
        {showLlSignoff && (
          <div className="bg-white rounded-lg border border-indigo-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-5 h-5 text-indigo-600" /> Sign off closure
            </h2>
            <p className="text-xs text-slate-600 mb-3">
              The operator submitted rework and is asking you to close this KO. Review the details below and Approve to mark it CLOSED, or Reject with a reason.
            </p>
            <div className="text-sm text-slate-700 space-y-2 mb-3">
              {ko.rework_type && (
                <div>
                  <span className="text-slate-500">Rework type:</span>{' '}
                  <span className="font-medium">{reworkTypes.find(r => r.id === ko.rework_type)?.label_en || `#${ko.rework_type}`}</span>
                </div>
              )}
              {ko.reworked_by && (
                <div>
                  <span className="text-slate-500">Submitted by:</span>{' '}
                  <span className="font-medium">user #{ko.reworked_by}</span>
                </div>
              )}
              {ko.rework_comment && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Operator comment</p>
                  <p className="text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2">{ko.rework_comment}</p>
                </div>
              )}
              {ko.after_rework_photo && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">After-rework photo</p>
                  <a href={ko.after_rework_photo} target="_blank" rel="noreferrer" className="inline-block">
                    <img src={ko.after_rework_photo} alt="After rework" className="max-h-48 rounded border border-slate-200" />
                  </a>
                </div>
              )}
            </div>
            {error && (
              <div className="p-2 rounded bg-red-50 border border-red-200 text-red-800 text-xs flex items-center gap-1 mb-2">
                <X className="w-3 h-3" />{error}
              </div>
            )}
            {ok && (
              <div className="p-2 rounded bg-green-50 border border-green-200 text-green-800 text-xs flex items-center gap-1 mb-2">
                <CheckCircle2 className="w-3 h-3" />{ok}
              </div>
            )}
            <div className="flex gap-2">
              <Button
                onClick={() => decideClosure('approve')}
                disabled={busy}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {busy ? '…' : 'Approve closure'}
              </Button>
              <Button
                onClick={() => decideClosure('reject')}
                disabled={busy}
                variant="outline"
                className="border-red-300 text-red-700 hover:bg-red-50"
              >
                Reject
              </Button>
            </div>
          </div>
        )}

        {/* Operator action panel — Mark as Reworked. */}
        {canRequestClosure && (
          <div className="bg-white rounded-lg border border-amber-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2 mb-2">
              <Wrench className="w-5 h-5 text-amber-600" /> Mark as Reworked
            </h2>
            <p className="text-xs text-slate-600 mb-3">
              Submit the rework details for a Line Leader to sign off. The KO stays open until they approve.
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Rework type *</span>
                <select
                  value={reworkTypeId}
                  onChange={e => setReworkTypeId(e.target.value)}
                  disabled={busy}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded"
                >
                  <option value="">Select rework type…</option>
                  {reworkTypes.map(rt => (
                    <option key={rt.id} value={rt.id}>{rt.label_en || rt.code}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Comment</span>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  disabled={busy}
                  rows={3}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded"
                  placeholder="Describe what you did (optional)"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">After-rework photo URL</span>
                <input
                  value={photoUrl}
                  onChange={e => setPhotoUrl(e.target.value)}
                  disabled={busy}
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded"
                  placeholder="Optional Cloudinary URL"
                />
              </label>
              {error && (
                <div className="p-2 rounded bg-red-50 border border-red-200 text-red-800 text-xs flex items-center gap-1">
                  <X className="w-3 h-3" />{error}
                </div>
              )}
              {ok && (
                <div className="p-2 rounded bg-green-50 border border-green-200 text-green-800 text-xs flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />{ok}
                </div>
              )}
              <Button
                onClick={submitClosure}
                disabled={busy}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {busy ? 'Submitting…' : 'Submit for Line Leader Closure'}
              </Button>
            </div>
          </div>
        )}

        {isClosureWaiting && (
          <div className="bg-white rounded-lg border border-amber-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" /> Awaiting Line Leader closure signature
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              You submitted the rework. A Line Leader needs to sign off on the closure before the KO is marked Closed.
            </p>
            {ko.rework_type && (
              <div className="mt-2 text-sm">
                <span className="text-slate-500">Your rework type:</span>{' '}
                <span className="font-medium">
                  {reworkTypes.find(r => r.id === ko.rework_type)?.label_en || `#${ko.rework_type}`}
                </span>
              </div>
            )}
            {ko.rework_comment && (
              <div className="mt-2">
                <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Your comment</p>
                <p className="text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2 text-sm">{ko.rework_comment}</p>
              </div>
            )}
          </div>
        )}

        {isClosed && (
          <div className="bg-white rounded-lg border border-green-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" /> KO Closed
            </h2>
            <div className="text-sm text-slate-700 mt-2 space-y-1">
              <div><span className="text-slate-500">Closed at:</span> {fmtWhen(ko.reworked_at)}</div>
              {ko.rework_type && (
                <div><span className="text-slate-500">Rework type:</span>{' '}
                  {reworkTypes.find(r => r.id === ko.rework_type)?.label_en || `#${ko.rework_type}`}
                </div>
              )}
              {ko.rework_comment && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Comment</p>
                  <p className="text-slate-800 whitespace-pre-wrap bg-slate-50 rounded p-2">{ko.rework_comment}</p>
                </div>
              )}
              {ko.after_rework_photo && (
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">After-rework photo</p>
                  <a href={ko.after_rework_photo} target="_blank" rel="noreferrer" className="inline-block">
                    <img src={ko.after_rework_photo} alt="After rework" className="max-h-48 rounded border border-slate-200" />
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {ko.rejection_reason && (
          <div className="bg-white rounded-lg border border-red-200 p-4">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <X className="w-5 h-5 text-red-600" /> Closure rejected
            </h2>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{ko.rejection_reason}</p>
          </div>
        )}
      </div>
    </div>
  );
}
